import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Fade } from "react-reveal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { catchAsync, checkResponse } from "utilities/utilities";
import { ADD_TALENT, TALENT_DETAILS, UPLOAD_FILE } from "services/ApiCalls";
import Loading from "Component/common/Loading";

// Validation schema
const schema = z.object({
  name: z.string().min(1, "Name is required"),
  media: z.string().optional(), // URL of uploaded icon
});

const Addtalent = () => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [data, setData] = useState();
  const [preview, setPreview] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const EditId = !!id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      media: "",
    },
  });

  // Watch media field for preview
  const mediaUrl = watch("media");
  useEffect(() => {
    if (mediaUrl) {
      setPreview(mediaUrl);
    }
  }, [mediaUrl]);

  // Media upload handler
  const onMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploadLoading(true);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const res = await UPLOAD_FILE(formData);
      if (res?.status === 200) {
        const uploadedFile = res?.data?.data?.files[0];
        setValue("media", uploadedFile); // set URL in form
        setPreview(uploadedFile);
      }
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setImageUploadLoading(false);
    }
  };

  const submitHandler = async (formData) => {
    setSubmitLoader(true);

    const requestBody = id
      ? { _id: id, name: formData.name, icon: formData.media }
      : { name: formData.name, icon: formData.media };

    const res = await ADD_TALENT(requestBody);

    checkResponse({
      res,
      showSuccess: true,
      navigateUrl: "/talent",
      navigate,
      setLoader: setSubmitLoader,
    });
  };

  const getData = catchAsync(async () => {
    const res = await TALENT_DETAILS(id);
    checkResponse({
      res,
      setData: (data) => {
        setData(data);

        reset({
          name: data.name || "",
          media: data.icon || "",
        });

        if (data.icon) {
          setPreview(data.icon);
        }
      },
    });
  });

  useEffect(() => {
    if (id) getData();
  }, [id]);

  return (
    <Fade>
      <section className="addresources comon_dashpad">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="comom_fillter_set text-start">
            <div className="resource_headwrap d-flex align-items-center justify-content-between">
              <h5 className="fillter_head">
                {EditId ? "Edit Talent" : "Add Talent"}
              </h5>
            </div>

            <div className="breadcrumb_set mt-3">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/talent">Manage Talent</Link>
                </BreadcrumbItem>
                <BreadcrumbItem active>
                  {EditId ? "Edit Talent" : "Add Talent"}
                </BreadcrumbItem>
              </Breadcrumb>
            </div>

            <div className="addresources_bottom_form rounded-3 p-3">
              <div className="author_form">
                <Row>
                  {/* Name Input */}
                  <Col lg={6}>
                    <FormGroup>
                      <Label for="name">Talent</Label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            id="name"
                            placeholder=""
                            {...field}
                            style={{ color: "white" }}
                          />
                        )}
                      />
                      {errors.name && (
                        <span style={{ color: "red" }}>
                          {errors.name.message}
                        </span>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Icon Upload */}
                  <Col lg={6}>
                    <FormGroup>
                      <Label for="media">Icon</Label>
                      <Input
                        type="file"
                        id="media"
                        accept="image/*"
                        onChange={onMediaUpload}
                      />
                      {imageUploadLoading && (
                        <p style={{ color: "white" }}>Uploading...</p>
                      )}

                      {preview && (
                        <div
                          className="mt-2 position-relative"
                          style={{ display: "inline-block" }}
                        >
                          <img
                            src={preview}
                            alt="Preview"
                            style={{ maxWidth: "150px", borderRadius: "8px" }}
                          />

                          {/* CROSS BUTTON */}
                          <button
                            type="button"
                            onClick={() => {
                              setPreview(null);
                              setValue("media", "");
                            }}
                            style={{
                              position: "absolute",
                              top: -10,
                              right: -10,
                              background: "red",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: 25,
                              height: 25,
                              cursor: "pointer",
                              fontWeight: "bold",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Submit Button */}
                <Col lg={6} className="mt-4">
                  <Button className="export_csv w-100" style={{ height: 50 }}>
                    {submitLoader ? <Loading fullSize={true} /> : "Submit"}
                  </Button>
                </Col>
              </div>
            </div>
          </div>
        </form>
      </section>
    </Fade>
  );
};

export default Addtalent;
